import React, { ChangeEvent, useCallback, useMemo, useState } from "react";

import Divider from "antd/es/divider";
import Input from "antd/es/input";
import Select, { LabeledValue, SelectProps } from "antd/es/select";
import sortBy from "lodash-es/sortBy";
import compose from "lodash/fp/compose";
import map from "lodash/fp/map";
import { useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import { ClassName } from "@app/types/ui/ClassName";
import { EMAIL_REGEX } from "@app/util/regexp";

import styles from "./EmailSelect.module.scss";

const buildOptions = compose(
  map<string, LabeledValue>((email) => ({
    label: email,
    value: email,
  })),
  sortBy
);

interface Props extends ClassName {
  initialEmails: string[];
  onChange?: SelectProps<string[]>["onChange"];
  value?: string[];
}

export const EmailSelect = ({ className, initialEmails, onChange, value }: Props) => {
  const intl = useIntl();

  const [emails, setEmails] = useState(initialEmails);

  const options = useMemo<LabeledValue[]>(() => buildOptions(emails), [emails]);

  const [newEmail, setNewEmail] = useState("");

  const handleNewEmail = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setNewEmail(event.target.value),
    [setNewEmail]
  );

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (["Backspace", "Enter"].includes(event.key)) {
      event.stopPropagation();
    }
  }, []);

  const addHandler = useCallback(() => {
    setEmails([...emails, newEmail]);
    setNewEmail("");
  }, [emails, newEmail, setEmails, setNewEmail]);

  return (
    <Select<string[]>
      className={className}
      dropdownRender={(menu) => (
        <div>
          {menu}
          <Divider className={styles.divider} />
          <div className={styles.form}>
            <Input
              className={styles.input}
              onChange={handleNewEmail}
              onKeyDown={handleKeyDown}
              type="email"
              value={newEmail}
            />
            <Button disabled={!EMAIL_REGEX.test(newEmail)} onClick={addHandler} type="link">
              {intl.formatMessage({
                id: "email.selector.addRecipient",
                defaultMessage: "Add as new recipient",
                description: "Add recipient button in selector",
              })}
            </Button>
          </div>
        </div>
      )}
      mode="tags"
      onChange={onChange}
      options={options}
      value={value}
    />
  );
};
